import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const medallia = defineConnector({
  identifier: "medallia",
  name: "Medallia",
  tagLine: "Customer experience management",
  logo,
  protocol: "http",
  tags: ["productivity"],
  defaultUrlPattern: ({ apiHostUrl }) => domainToRegex(apiHostUrl),
  auth: oauthAuth,
  steps: [
    {
      title: "Enter your Medallia instance details",
      items: [
        {
          kind: "markdown",
          content: "Enter the details for your Medallia instance.",
        },
        {
          kind: "input",
          label: "Domain",
          placeholder: "instance.medallia.com",
          required: true,
          target: { kind: "configuration", field: "reportingInstance" },
          helperText:
            "The base domain for your Medallia instance (e.g. https://instance.medallia.com/oauth)",
        },
        {
          kind: "input",
          label: "Tenant Name",
          placeholder: "acme",
          required: true,
          target: { kind: "configuration", field: "tenantName" },
          helperText: "The tenant name of your Medallia account",
        },
        {
          kind: "input",
          label: "API Host URL",
          placeholder: "fo-acme.apis.medallia.eu",
          required: true,
          target: { kind: "configuration", field: "apiHostUrl" },
          helperText:
            "The base API endpoint (gateway URL) for your Medallia account (e.g. https://fo-acme.apis.medallia.eu)",
        },
        {
          kind: "input",
          label: "",
          placeholder: "your-gatewayUrl",
          required: true,
          target: { kind: "configuration", field: "gatewayUrl" },
        },
      ],
    },
    {
      title: "Create a Medallia app",
      items: [
        {
          kind: "markdown",
          content:
            "In your Medallia admin console, go to **Administration → API Credentials** and create new OAuth client credentials.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content: "In your app’s settings, add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/medallia/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "input",
          label: "Client ID",
          placeholder: "Paste your Client ID",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          placeholder: "Paste your Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Medallia account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Medallia account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: (configuration) =>
      `https://${configuration.reportingInstance}/oauth/${configuration.tenantName}/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: (configuration) => `https://${configuration.apiHostUrl}/`,
  },
  publicEnvVars: ({
    apiHostUrl,
    gatewayUrl,
    reportingInstance,
    tenantName,
  }) => ({
    MEDALLIA_API_HOST_URL: apiHostUrl || "",
    MEDALLIA_GATEWAY_URL: gatewayUrl || "",
    MEDALLIA_REPORTING_INSTANCE: reportingInstance || "",
    MEDALLIA_TENANT_NAME: tenantName || "",
  }),
});
